/**
 * /hk/training/supervisor · Supervisor Trainee Board
 * ────────────────────────────────────────────────────────────────────
 * Shows every trainee's progress, badges, scores and last activity.
 * Supervisor actions: skip module, reset, comment. Backend endpoints
 * (permission-gated): /api/oa/v1/training/supervisor/*
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Trophy, Loader2, RotateCcw, MessageSquare, SkipForward, ArrowLeft,
  ShieldCheck, X, Clock, Award,
} from "lucide-react";
import { toast } from "sonner";
import { VoiceRegistryProvider, OfflineSyncIndicator } from "@/components/operational/hk_os";

const API = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: `${API}/api/oa/v1`, withCredentials: true });

const STATUS_TONE = {
  in_progress:          "bg-sky-100 text-sky-900 border-sky-300",
  awaiting_certification:"bg-amber-100 text-amber-900 border-amber-300",
  certified:            "bg-emerald-100 text-emerald-900 border-emerald-300",
  completed:            "bg-emerald-100 text-emerald-900 border-emerald-300",
};

function StatusPill({ status, testid }) {
  const tone = STATUS_TONE[status] || "bg-slate-100 text-slate-800 border-slate-300";
  return (
    <span data-testid={testid}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-widest ${tone}`}>
      {(status || "unknown").replace(/_/g, " ")}
    </span>
  );
}

function TraineeRow({ t, modules, onOpen, testid }) {
  const modsDone = t.modules_completed || 0;
  const modsTot = t.modules_total || modules.length || 8;
  return (
    <tr className="border-t border-slate-200 hover:bg-slate-50 cursor-pointer"
        data-testid={testid}
        onClick={() => onOpen(t)}>
      <td className="px-4 py-3">
        <div className="text-[14px] font-bold text-slate-900">{t.attendant_email || "—"}</div>
        <div className="text-[11px] text-slate-500 truncate max-w-[220px]">
          {t.hotel_id}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="w-24 h-2 rounded-full bg-slate-200 overflow-hidden">
            <div className="h-full bg-sky-500"
                  style={{ width: `${(t.progress_pct || 0) * 100}%` }} />
          </div>
          <span className="text-[12px] font-bold text-slate-700 tabular-nums w-16">
            {modsDone}/{modsTot}
          </span>
        </div>
      </td>
      <td className="px-4 py-3 tabular-nums text-[14px] font-bold text-slate-800">
        {(t.score || 0).toFixed(0)}%
      </td>
      <td className="px-4 py-3 tabular-nums text-[12px] text-slate-600">
        {t.attempts_total} atts · {t.mistakes_total} miss
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          {(t.badges || []).slice(0, 4).map((b) => (
            <span key={b.key} title={b.label}
              className="inline-flex items-center rounded-full bg-amber-100 text-amber-900 px-2 py-0.5 text-[11px] font-bold">
              <Trophy className="w-3 h-3 mr-1" /> {b.label.split(" ")[0]}
            </span>
          ))}
          {(t.badges || []).length > 4 && (
            <span className="text-[11px] text-slate-500">+{t.badges.length - 4}</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <StatusPill status={t.status}
                     testid={`${testid}-status`} />
      </td>
      <td className="px-4 py-3 text-[11px] text-slate-500 tabular-nums">
        {t.updated_at ? new Date(t.updated_at).toLocaleString() : "—"}
      </td>
    </tr>
  );
}

function TraineeDrawer({ trainee, modules, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState("");
  const [full, setFull] = useState(null);

  // Fetch the full session (with progress_modules + supervisor_comments)
  // when the drawer opens. The board endpoint returns a lean summary.
  useEffect(() => {
    if (!trainee) { setFull(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await client.get(`/training/supervisor/session/${trainee.id}`);
        if (!cancelled) setFull(data.session);
      } catch {
        if (!cancelled) setFull(null);
      }
    })();
    return () => { cancelled = true; };
  }, [trainee]);

  if (!trainee) return null;

  // Merge lean board row with full server payload (server wins).
  const view = { ...trainee, ...(full || {}) };

  const doReset = async () => {
    if (!confirm("Reset this trainee's progress?")) return;
    setBusy(true);
    try {
      const { data } = await client.post(`/training/supervisor/session/${trainee.id}/reset`);
      setFull(data.session);
      onChanged(data.session);
      toast.success("Progress reset");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  const doSkip = async (mid) => {
    setBusy(true);
    try {
      const { data } = await client.post(`/training/supervisor/session/${trainee.id}/skip/${mid}`);
      setFull(data.session);
      onChanged(data.session);
      toast.success(`Skipped ${mid}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Skip failed");
    } finally { setBusy(false); }
  };

  const submitComment = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try {
      const { data } = await client.post(`/training/supervisor/session/${trainee.id}/comment`,
                                          { comment: comment.trim() });
      setFull(data.session);
      onChanged(data.session);
      setComment("");
      toast.success("Comment added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Comment failed");
    } finally { setBusy(false); }
  };

  const prog = view.progress_modules || {};

  return (
    <aside
      data-testid="trainee-drawer"
      className="fixed inset-y-0 right-0 z-40 w-full max-w-lg bg-white border-l border-slate-200 shadow-2xl overflow-y-auto"
    >
      <header className="sticky top-0 bg-white border-b border-slate-200 p-5 flex items-start gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Trainee</div>
          <div className="text-[18px] font-bold text-slate-900 truncate">{view.attendant_email}</div>
          <div className="mt-1">
            <StatusPill status={view.status} />
          </div>
        </div>
        <button type="button" onClick={onClose}
                  className="ml-auto rounded-full border border-slate-200 p-1.5 hover:bg-slate-100"
                  data-testid="trainee-drawer-close">
          <X className="w-4 h-4 text-slate-600" />
        </button>
      </header>

      <div className="p-5 space-y-6">
        {/* Metrics */}
        <section>
          <div className="grid grid-cols-3 gap-3">
            <MetricBox icon={ShieldCheck} label="Score" value={`${(view.score || 0).toFixed(0)}%`} />
            <MetricBox icon={Clock} label="Attempts" value={view.attempts_total || 0} />
            <MetricBox icon={RotateCcw} label="Mistakes" value={view.mistakes_total || 0} />
          </div>
        </section>

        {/* Modules */}
        <section>
          <h3 className="text-[13px] font-bold uppercase tracking-widest text-slate-500 mb-2">
            Modules
          </h3>
          <div className="space-y-2">
            {modules.map((m) => {
              const info = prog[m.id] || {};
              const status = info.status || "not_started";
              return (
                <div key={m.id}
                      className="flex items-center gap-3 rounded-2xl border border-slate-200 p-3"
                      data-testid={`drawer-module-${m.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-bold text-slate-900 truncate">
                      {m.order}. {m.title}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {info.attempts || 0} atts · {info.mistakes || 0} miss
                      {info.score !== undefined && ` · ${info.score}%`}
                      {info.skipped_by_supervisor && (
                        <span className="ml-1 text-amber-700">· skipped by {info.skipped_by_supervisor}</span>
                      )}
                    </div>
                  </div>
                  <StatusPill status={status} />
                  {status !== "completed" && !m.is_assessment && (
                    <button type="button"
                              disabled={busy}
                              onClick={() => doSkip(m.id)}
                              className="rounded-full border border-slate-300 text-slate-700 px-2 py-1 text-[11px] font-semibold hover:bg-slate-100 disabled:opacity-50"
                              data-testid={`drawer-skip-${m.id}`}>
                      <SkipForward className="inline w-3 h-3 mr-0.5" /> Skip
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Badges */}
        {(view.badges || []).length > 0 && (
          <section>
            <h3 className="text-[13px] font-bold uppercase tracking-widest text-slate-500 mb-2">
              Badges
            </h3>
            <div className="flex flex-wrap gap-2">
              {view.badges.map((b) => (
                <span key={b.key}
                        className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 text-amber-900 px-2.5 py-1 text-[12px] font-bold"
                        data-testid={`drawer-badge-${b.key}`}>
                  <Award className="w-3 h-3" /> {b.label}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Supervisor comments */}
        <section>
          <h3 className="text-[13px] font-bold uppercase tracking-widest text-slate-500 mb-2">
            Comments
          </h3>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {(view.supervisor_comments || []).length === 0 && (
              <div className="text-[12px] text-slate-500">No comments yet.</div>
            )}
            {(view.supervisor_comments || []).map((c, i) => (
              <div key={i}
                    data-testid={`drawer-comment-${i}`}
                    className="rounded-xl border border-slate-200 p-3 bg-slate-50 text-[13px] text-slate-800">
                <div className="text-[11px] font-bold text-slate-500">
                  {c.by} · {new Date(c.at).toLocaleString()}
                </div>
                <div>{c.comment}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <input
              type="text" value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment"
              data-testid="drawer-comment-input"
              className="flex-1 rounded-full border border-slate-300 px-4 py-2 text-[13px]"
            />
            <button type="button"
                      onClick={submitComment}
                      disabled={busy || !comment.trim()}
                      data-testid="drawer-comment-submit"
                      className="rounded-full bg-sky-600 text-white px-4 py-2 text-[13px] font-bold hover:bg-sky-700 disabled:opacity-50">
              <MessageSquare className="inline w-3.5 h-3.5 mr-1" /> Add
            </button>
          </div>
        </section>

        {/* Reset */}
        <section className="pt-4 border-t border-slate-200">
          <button type="button" onClick={doReset} disabled={busy}
                    data-testid="drawer-reset"
                    className="w-full rounded-2xl border-2 border-red-300 bg-red-50 text-red-800 py-3 font-bold hover:bg-red-100 disabled:opacity-50">
            <RotateCcw className="inline w-4 h-4 mr-2" /> Reset training
          </button>
        </section>
      </div>
    </aside>
  );
}

function MetricBox({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-center">
      <Icon className="w-5 h-5 mx-auto text-slate-500 mb-1" strokeWidth={2.4} />
      <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
        {label}
      </div>
      <div className="text-[18px] font-bold text-slate-900 tabular-nums">
        {value}
      </div>
    </div>
  );
}

export default function HkTrainingSupervisor() {
  return (
    <VoiceRegistryProvider locale="bn-IN" autoWarmup={false}>
      <HkTrainingSupervisorBody />
    </VoiceRegistryProvider>
  );
}

function HkTrainingSupervisorBody() {
  const [board, setBoard] = useState(null);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await client.get("/training/supervisor/board");
      setBoard(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Cannot load board (permission?)");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onChanged = (updated) => {
    if (!updated) return;
    setBoard((prev) => prev && ({
      ...prev,
      trainees: prev.trainees.map((t) => t.id === updated.id ? { ...t, ...normalizeSession(updated, prev.modules) } : t),
    }));
    setSelected((prev) => prev && ({ ...prev, ...normalizeSession(updated, board?.modules || []) }));
  };

  const summary = useMemo(() => {
    const arr = board?.trainees || [];
    return {
      total: arr.length,
      certified: arr.filter((t) => t.status === "certified").length,
      inProgress: arr.filter((t) => t.status === "in_progress").length,
      avgScore: arr.length === 0 ? 0
        : Math.round(arr.reduce((s, t) => s + (t.score || 0), 0) / arr.length),
    };
  }, [board]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="rounded-2xl border-2 border-red-300 bg-red-50 text-red-800 p-6 max-w-lg"
              data-testid="supervisor-error">
          <div className="text-[18px] font-bold mb-2">Cannot load board</div>
          <div className="text-[14px]">{error}</div>
          <a href="/hk/training" className="mt-3 inline-block text-sky-700 underline">Back to training</a>
        </div>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center gap-3 text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin" /> Loading board…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-3">
          <a href="/hk/training"
              className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-100"
              data-testid="supervisor-back">
            <ArrowLeft className="inline w-3.5 h-3.5 mr-1" /> Back
          </a>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-sky-600">
              HK-2 · Training
            </div>
            <h1 className="text-[24px] font-black text-slate-900"
                data-testid="supervisor-title">Supervisor Trainee Board</h1>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <OfflineSyncIndicator />
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-6 space-y-4">
        <div className="grid sm:grid-cols-4 gap-3">
          <MetricBox icon={ShieldCheck} label="Trainees" value={summary.total} />
          <MetricBox icon={Trophy}      label="Certified" value={summary.certified} />
          <MetricBox icon={Clock}       label="In progress" value={summary.inProgress} />
          <MetricBox icon={Award}       label="Avg score" value={`${summary.avgScore}%`} />
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-slate-50 text-[11px] font-bold uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-3">Trainee</th>
                <th className="px-4 py-3">Progress</th>
                <th className="px-4 py-3">Score</th>
                <th className="px-4 py-3">Attempts</th>
                <th className="px-4 py-3">Badges</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Last activity</th>
              </tr>
            </thead>
            <tbody>
              {board.trainees.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-slate-500 text-[14px]"
                        data-testid="supervisor-empty">
                    No trainees yet. Ask staff to open /hk/training on their device.
                  </td>
                </tr>
              )}
              {board.trainees.map((t) => (
                <TraineeRow key={t.id} t={t} modules={board.modules}
                              onOpen={setSelected}
                              testid={`trainee-row-${t.id}`} />
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {selected && (
        <TraineeDrawer trainee={selected} modules={board.modules}
                        onClose={() => setSelected(null)}
                        onChanged={(s) => { onChanged(s); }} />
      )}
    </div>
  );
}

/** Normalize a session document returned by supervisor endpoints so it
 *  slots into the board row shape. */
function normalizeSession(s, modules) {
  const prog = s.progress_modules || {};
  const modsDone = modules.filter((m) => prog[m.id]?.status === "completed").length;
  return {
    ...s,
    modules_completed: modsDone,
    modules_total: modules.length,
    progress_pct: modules.length ? modsDone / modules.length : 0,
  };
}
